/**
 * Small hook + wrapper for employer-side auth.
 *
 * Employers are a distinct account type from candidates — they get their own
 * JWT cookie (`employer_token`) and go through admin verification before they
 * can browse the directory. This module gives page components a clean way to
 * check the current session without every page re-implementing the fetch.
 */
import { useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function useEmployer() {
  const [state, setState] = useState({ employer: null, loading: true });

  const refetch = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/employer/me`, {
        withCredentials: true,
      });
      setState({ employer: r.data, loading: false });
    } catch {
      setState({ employer: null, loading: false });
    }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);
  return { ...state, refetch };
}

/**
 * Wrap a page in this to require a verified employer session. Behaviour:
 *   · no employer_token cookie   → redirect to /employer/login
 *   · signed in but not verified → render the passed `pendingElement`
 *   · verified                   → render children
 *
 * Pass `allowPending` to let pending/unverified employers into the page
 * (still logged-in required — just not verified). Used by the Candidates
 * Store which shows anonymized previews to pending employers.
 */
export default function EmployerProtected({ children, pendingElement = null, allowPending = false }) {
  const { employer, loading } = useEmployer();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
           style={{ background: "#F8FAFC" }}>
        <div className="font-mono-rm text-[11px] uppercase tracking-widest"
             style={{ color: "#94A3B8" }}>
          · Checking your employer session
        </div>
      </div>
    );
  }
  if (!employer) {
    // Preserve the intended destination so we can send them back after login.
    return <Navigate to="/employer/login" state={{ from: location.pathname }} replace />;
  }
  if (employer.status !== "verified" && !allowPending) {
    return pendingElement;
  }
  return children;
}
